/* eslint-disable */
import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useCurrentUser } from "./AuthWrapper";
import { LOGO_B64, C } from "./ui/tokens";
import { DataPanel } from "./ui/DataPanel";
import { PersonWorkspace, DEFAULT_SCENARIO } from "./tabs/PersonWorkspace";
import { migrateClients } from "./engine/migrate";
import { SettingsTab } from "./tabs/SettingsTab";

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const STORAGE_KEY = "204plans_clients_v1";
const SETTINGS_KEY = "204plans_settings_v1";

const SAMPLE_CLIENTS = {
  "Lastname, Firstname": {
    first_name: "Firstname", last_name: "Lastname", dob: "1957-04-04", is_prospect: false,
    accounts: [
      { id: "acct1", account_number: "1234", balance: 100000, is_annuity: false, is_inherited_ira: false },
      { id: "acct2", account_number: "2345", balance: 150000, is_annuity: false, is_inherited_ira: true, descendent_dob: "1980-04-25", dob_selection: 0, beneficiary_type: "edb" },
    ],
    scenario: { ...DEFAULT_SCENARIO },
  }
};

const DEFAULT_SETTINGS = { logoDataUrl: null, teamMembers: [] };

export default function App() {
  const { instance } = useMsal();
  const user = useCurrentUser();
  const [clients, setClientsRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return migrateClients(saved ? JSON.parse(saved) : SAMPLE_CLIENTS);
    } catch { return migrateClients(SAMPLE_CLIENTS); }
  });
  // Any write (including JSON import) passes through the migration so the shape stays uniform.
  const setClients = (updater) => setClientsRaw(prev => migrateClients(typeof updater === "function" ? updater(prev) : updater));
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  const [view, setView] = useState("people");

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(clients)); } catch {} }, [clients]);
  useEffect(() => { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch {} }, [settings]);

  const logoSrc = settings.logoDataUrl || `data:image/jpeg;base64,${LOGO_B64}`;

  const navItems = [
    { id: "people", icon: "👥", label: "Clients & Prospects" },
    { id: "settings", icon: "⚙️", label: "Settings" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%)", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Google Fonts */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500&family=DM+Serif+Display&display=swap');`}</style>

      {/* Sidebar */}
      <div style={{
        width: 260, minHeight: "100vh", background: "#fff",
        borderRight: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column", position: "sticky", top: 0,
        boxShadow: "2px 0 8px rgba(0,0,0,.04)", flexShrink: 0,
      }}>
        {/* Brand */}
        <div style={{ padding: "28px 24px 20px", borderBottom: `1px solid ${C.borderLight}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <img src={logoSrc} alt="Harness Financial" style={{ width: 48, height: 37, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.navy, letterSpacing: "-0.03em" }}>204Plans</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Harness Financial, LLC</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, background: C.surface, borderRadius: 8, padding: "6px 10px" }}>
            Section 204 RMD Planning Tool
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "16px 12px" }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: C.textMuted, padding: "0 12px", marginBottom: 8 }}>Navigation</div>
          {navItems.map(item => (
            <button key={item.id} onClick={() => setView(item.id)} style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%",
              padding: "10px 12px", borderRadius: 10, border: "none", cursor: "pointer",
              background: view === item.id ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 100%)` : "transparent",
              color: view === item.id ? "#fff" : C.textSec,
              fontSize: 14, fontWeight: view === item.id ? 700 : 500,
              fontFamily: "inherit", marginBottom: 4, textAlign: "left",
              transition: "all .15s",
            }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div>
          <DataPanel clients={clients} setClients={setClients} />
          {/* User + Sign Out */}
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.borderLight}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
              background: `linear-gradient(135deg, ${C.navy}, #2d5a87)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: "#fff",
            }}>
              {user.name ? user.name.split(" ").map(w => w[0]).slice(0, 2).join("") : "?"}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.name || "—"}
              </div>
              <div style={{ fontSize: 10, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.email || ""}
              </div>
            </div>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
              background: user.isAdmin ? "#fef3c7" : "#eff6ff",
              color: user.isAdmin ? "#92400e" : "#1e40af",
              textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0,
            }}>
              {user.isAdmin ? "Admin" : "Advisor"}
            </span>
          </div>
          <button
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
            style={{
              width: "100%", padding: "7px 12px", borderRadius: 8, fontSize: 11,
              fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              border: `1px solid ${C.borderLight}`, background: "#fff",
              color: C.textSec, display: "flex", alignItems: "center",
              justifyContent: "center", gap: 6,
            }}
          >
            <span>⎋</span> Sign Out
          </button>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px 20px", background: C.surface, borderTop: `1px solid ${C.borderLight}` }}>
          <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.5 }}>
            Securities and advisory services offered through LPL Financial, a registered investment advisor, Member FINRA/SIPC.<br /><br />
            Section 204 Plan is a trademark and patent pending of Harness Financial, LLC.
          </div>
        </div>
        </div>{/* end DataPanel wrapper */}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: "32px 28px", maxWidth: 1100, margin: "0 auto", minWidth: 0 }}>
        {view === "people" && <PersonWorkspace clients={clients} setClients={setClients} settings={settings} />}
        {view === "settings" && <SettingsTab settings={settings} setSettings={setSettings} />}

        {/* Disclaimer Footer */}
        <div style={{ background: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 10, padding: "16px 20px", marginTop: 32 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Harness Financial, LLC | Section 204 Planning</div>
          <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.6 }}>
            This report is for educational and planning purposes only. Actual RMD amounts depend on year-end account values, IRS table updates, and individual circumstances. Consult a qualified tax professional before making distribution decisions. Securities and advisory services offered through LPL Financial, a registered investment advisor, Member FINRA/SIPC. Section 204 Plan is a trademark and patent pending of Harness Financial, LLC.
          </div>
        </div>
      </div>
    </div>
  );
}
